import { useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Tooltip, TooltipText } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';
import { normalizeStoredPath, resolveFileContentUrl } from '@/utils/fileUrl';
import { openStoredFileInNewTab } from '@/utils/openStoredFile';

type Props = {
  reason?: string | null;
  attachmentUrl?: string | null;
  compact?: boolean;
  showLabels?: boolean;
  showAttachment?: boolean;
  showView?: boolean;
  showDownload?: boolean;
  reasonMaxLength?: number;
  className?: string;
};

function attachmentFileName(url: string): string {
  const path = normalizeStoredPath(url) ?? url;
  const name = path.split('/').pop() ?? 'Document';
  return decodeURIComponent(name);
}

function TruncatedLeaveReason({
  text,
  maxLength = 48,
  className,
}: {
  text: string;
  maxLength?: number;
  className?: string;
}) {
  const trimmed = text.trim();
  const truncated = trimmed.length > maxLength ? `${trimmed.slice(0, maxLength - 1)}…` : trimmed;

  if (trimmed.length <= maxLength) {
    return <span className={className}>{truncated}</span>;
  }

  return (
    <Tooltip content={trimmed}>
      <span className={cn('cursor-default', className)}>{truncated}</span>
    </Tooltip>
  );
}

const linkClass =
  'font-medium text-primary-600 hover:underline dark:text-primary-400';

export function HrLeaveReasonAttachmentDisplay({
  reason,
  attachmentUrl,
  compact = false,
  showLabels = true,
  showAttachment = true,
  showView = true,
  showDownload = true,
  reasonMaxLength = compact ? 36 : 48,
  className,
}: Props) {
  const [viewing, setViewing] = useState(false);
  const reasonText = reason?.trim() ?? '';
  const attachment = attachmentUrl?.trim() ?? '';
  const hasReason = reasonText.length > 0;
  const hasAttachment = showAttachment && attachment.length > 0;

  if (!hasReason && !hasAttachment) return null;

  const fileName = hasAttachment ? attachmentFileName(attachment) : '';
  const showViewAction = showView;
  const showDownloadAction = showDownload;

  return (
    <div className={cn('space-y-1.5', className)}>
      {hasReason ? (
        <div className={cn(compact ? 'flex min-w-0 items-baseline gap-1.5' : 'space-y-0.5')}>
          {showLabels ? (
            <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">Reason</span>
          ) : null}
          <TruncatedLeaveReason
            text={reasonText}
            maxLength={reasonMaxLength}
            className={cn(
              'min-w-0 text-slate-700 dark:text-slate-200',
              compact ? 'text-xs' : 'text-sm',
            )}
          />
        </div>
      ) : null}
      {hasAttachment ? (
        <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', compact ? 'text-xs' : 'text-sm')}>
          {showLabels ? (
            <span className="text-xs text-slate-500 dark:text-slate-400">Attachment</span>
          ) : (
            <AttachFileIcon className="text-slate-400" sx={{ fontSize: 14 }} aria-hidden />
          )}
          <TooltipText
            text={fileName}
            className="max-w-[10rem] truncate text-slate-600 dark:text-slate-300"
            as="span"
          />
          {showViewAction || showDownloadAction ? (
            <span className="text-slate-300 dark:text-slate-600" aria-hidden>
              |
            </span>
          ) : null}
          {showViewAction ? (
            <button
              type="button"
              disabled={viewing}
              onClick={() => {
                setViewing(true);
                void openStoredFileInNewTab(attachment).finally(() => setViewing(false));
              }}
              className={cn(linkClass, 'disabled:opacity-50')}
            >
              {viewing ? 'Opening…' : 'View'}
            </button>
          ) : null}
          {showDownloadAction ? (
            <a
              href={resolveFileContentUrl(attachment, 'attachment')}
              className={linkClass}
            >
              Download
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
